import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend
} from 'recharts';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { apiProdDashboard, apiProdAlerts, apiProdComparison, apiListCustomers } from '../../api';
import {
  TrendingUp, AlertTriangle, Users, Euro, Package, Clock, Building2,
  CheckCircle2, XCircle, ChevronRight
} from 'lucide-react';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';

const fmtEur = (v) => '€' + (v || 0).toFixed(2);

// Chart constants extracted to avoid creating new object references on every render
const TREND_CHART_MARGIN = { top: 5, right: 20, left: 0, bottom: 5 };
const RANKING_CHART_MARGIN = { top: 5, right: 30, left: 80, bottom: 5 };
const AXIS_TICK_11 = { fontSize: 11 };
const AXIS_TICK_12 = { fontSize: 12 };
const TOOLTIP_STYLE = { borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 };
const LEGEND_WRAPPER_STYLE = { fontSize: 12 };
const BAR_RADIUS_RIGHT = [0, 4, 4, 0];

const Stat = ({ icon: Icon, label, value, color, subtitle }) => (
  <div className="p-4 bg-white rounded-xl border border-slate-200" data-testid={`stat-${label.replace(/\s+/g, '-').toLowerCase()}`}>
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="text-2xl font-bold text-slate-900 leading-tight tabular-nums">{value}</div>
        <div className="text-xs text-slate-500">{label}</div>
        {subtitle && <div className="text-[11px] text-slate-400 mt-0.5">{subtitle}</div>}
      </div>
    </div>
  </div>
);

const ProductionDashboard = ({ onOpenOperator }) => {
  const navigate = useNavigate();
  const [period, setPeriod] = useState('month');
  const [customerId, setCustomerId] = useState('all');
  const [customers, setCustomers] = useState([]);
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    apiListCustomers().then(setCustomers).catch(() => setCustomers([]));
  }, []);

  useEffect(() => {
    const cid = customerId === 'all' ? undefined : customerId;
    apiProdDashboard(period, cid).then(setData).catch(() => setData(null));
    apiProdAlerts(3).then(setAlerts).catch(() => setAlerts([]));
  }, [period, customerId]);

  if (!data) {
    return <div className="text-sm text-slate-500 p-6 text-center">Caricamento dashboard...</div>;
  }

  const t = data.totals;

  return (
    <div className="space-y-4" data-testid="production-dashboard">
      {/* Filters */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center bg-white border border-slate-200 rounded-md p-0.5">
            {[
              { v: 'day', label: 'Oggi' },
              { v: 'week', label: 'Settimana' },
              { v: 'month', label: 'Mese' },
              { v: 'all', label: 'Tutto' }
            ].map(o => (
              <button
                key={o.v}
                data-testid={`period-${o.v}`}
                onClick={() => setPeriod(o.v)}
                className={`text-xs px-3 py-1.5 rounded transition-colors ${
                  period === o.v ? 'bg-blue-700 text-white font-medium' : 'text-slate-600 hover:bg-slate-100'
                }`}
              >{o.label}</button>
            ))}
          </div>
          <Select value={customerId} onValueChange={setCustomerId}>
            <SelectTrigger data-testid="dashboard-customer-filter" className="h-9 w-[220px] text-sm bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i clienti</SelectItem>
              {customers.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.code} — {c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="text-xs text-slate-500">
          {data.start_date} → {data.end_date}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        <Stat icon={Package} label="Pezzi totali" value={t.pieces} color="bg-blue-100 text-blue-700" />
        <Stat icon={Clock} label="Ore lavorate" value={`${t.hours.toFixed(1)}h`} color="bg-amber-100 text-amber-700" />
        <Stat icon={Euro} label="Totale fatturato" value={fmtEur(t.total_eur)} color="bg-emerald-100 text-emerald-700" />
        <Stat icon={TrendingUp} label="Media €/ora" value={fmtEur(t.avg_eur_per_hour)} color="bg-indigo-100 text-indigo-700" />
        <Stat icon={Users} label="Operatori attivi" value={t.operators_active} color="bg-rose-100 text-rose-700" />
        <Stat icon={Building2} label="Clienti attivi" value={t.customers_active} color="bg-slate-100 text-slate-700" />
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-700 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold text-red-900 mb-2">Allarme produttività bassa</div>
              <div className="space-y-1.5">
                {alerts.map(a => (
                  <div key={a.operator_id} className="text-sm text-red-800 flex items-center gap-2 flex-wrap">
                    <span className="font-mono bg-red-100 px-1.5 py-0.5 rounded text-xs">{a.operator_alias}</span>
                    <span className="font-medium">{a.operator_name}</span>
                    <span className="text-red-600">— sotto target ({fmtEur(a.target)}/ora) da</span>
                    <span className="font-bold">{a.consecutive_below_days} giorni consecutivi</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Trend chart */}
      {data.trend.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="text-sm font-semibold text-slate-900 mb-3">Andamento giornaliero</div>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={data.trend} margin={TREND_CHART_MARGIN}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" tick={AXIS_TICK_11} stroke="#94a3b8" />
              <YAxis tick={AXIS_TICK_11} stroke="#94a3b8" />
              <Tooltip
                contentStyle={TOOLTIP_STYLE}
                formatter={(value, name) => {
                  if (name === '€/ora') return [fmtEur(value), '€/ora'];
                  if (name === 'Totale €') return [fmtEur(value), 'Totale €'];
                  return [value, name];
                }}
              />
              <Legend wrapperStyle={LEGEND_WRAPPER_STYLE} />
              <Line type="monotone" dataKey="total_eur" stroke="#2563eb" strokeWidth={2} name="Totale €" />
              <Line type="monotone" dataKey="avg_eur_per_hour" stroke="#10b981" strokeWidth={2} name="€/ora" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Customer summary */}
      {customerId === 'all' && data.customers.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="customers-summary">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              <Building2 className="w-4 h-4 text-slate-500" />
              Riepilogo per cliente
            </div>
            <div className="text-xs text-slate-500">{data.customers.length} clienti</div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase text-slate-600">Cliente</th>
                <th className="text-right px-3 py-2 text-xs font-semibold uppercase text-slate-600">Pezzi</th>
                <th className="text-right px-3 py-2 text-xs font-semibold uppercase text-slate-600">Ore</th>
                <th className="text-right px-3 py-2 text-xs font-semibold uppercase text-slate-600">Totale €</th>
                <th className="text-right px-3 py-2 text-xs font-semibold uppercase text-slate-600">Media €/ora</th>
                <th className="text-right px-3 py-2 text-xs font-semibold uppercase text-slate-600">Sessioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.customers.map(c => (
                <tr
                  key={c.customer_id}
                  className="hover:bg-blue-50 cursor-pointer transition-colors group"
                  onClick={() => navigate(`/production/customers/${c.customer_id}`)}
                  data-testid={`customer-summary-${c.customer_code}`}
                >
                  <td className="px-3 py-2.5">
                    <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded mr-1.5">{c.customer_code}</span>
                    <span className="text-slate-700 font-medium">{c.customer_name}</span>
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{c.pieces}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{c.hours.toFixed(1)}h</td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-blue-700">{fmtEur(c.total_eur)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-emerald-700">{fmtEur(c.avg_eur_per_hour)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-slate-600">
                    <div className="inline-flex items-center gap-1.5">
                      {c.sessions}
                      <ChevronRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-blue-600 transition-colors" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Operators ranking */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <div className="text-sm font-semibold text-slate-900">Classifica operatori</div>
          <div className="text-xs text-slate-500">{data.operators.length} operatori</div>
        </div>
        {data.operators.length === 0 ? (
          <div className="py-8 text-center text-sm text-slate-500">Nessuna sessione nel periodo selezionato</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase text-slate-600">#</th>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase text-slate-600">Operatore</th>
                <th className="text-right px-3 py-2 text-xs font-semibold uppercase text-slate-600">Pezzi</th>
                <th className="text-right px-3 py-2 text-xs font-semibold uppercase text-slate-600">Ore</th>
                <th className="text-right px-3 py-2 text-xs font-semibold uppercase text-slate-600">Totale</th>
                <th className="text-right px-3 py-2 text-xs font-semibold uppercase text-slate-600">€/ora</th>
                <th className="text-center px-3 py-2 text-xs font-semibold uppercase text-slate-600">Target</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.operators.map((o, idx) => (
                <tr key={o.operator_id} className="hover:bg-slate-50 cursor-pointer" onClick={() => onOpenOperator?.({ id: o.operator_id, alias: o.operator_alias })}>
                  <td className="px-3 py-2.5 text-slate-500 font-medium">{idx + 1}</td>
                  <td className="px-3 py-2.5">
                    <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded mr-1.5">{o.operator_alias}</span>
                    <span className="text-slate-700">{o.operator_name}</span>
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{o.pieces}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{o.hours.toFixed(1)}h</td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-blue-700">{fmtEur(o.total_eur)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-semibold">
                    {(() => {
                      let cls = 'text-slate-700';
                      if (o.on_target === true) cls = 'text-emerald-700';
                      else if (o.on_target === false) cls = 'text-red-700';
                      return <span className={cls}>{fmtEur(o.avg_eur_per_hour)}</span>;
                    })()}
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    {o.target > 0 ? (
                      o.on_target ? (
                        <div className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
                          <CheckCircle2 className="w-3 h-3" />
                          {fmtEur(o.target)}
                        </div>
                      ) : (
                        <div className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-800">
                          <XCircle className="w-3 h-3" />
                          {fmtEur(o.target)}
                        </div>
                      )
                    ) : <span className="text-xs text-slate-400">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export const ComparisonView = () => {
  const [data, setData] = useState([]);
  const [anonymized, setAnonymized] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState('all');

  useEffect(() => {
    apiListCustomers().then(setCustomers).catch(() => setCustomers([]));
  }, []);

  useEffect(() => {
    const params = { anonymized };
    if (customerId !== 'all') params.customer_id = customerId;
    apiProdComparison(params).then(setData).catch(() => setData([]));
  }, [anonymized, customerId]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-sm font-semibold text-slate-900">Confronto operatori</div>
          <div className="text-xs text-slate-500">Vista comparativa delle performance</div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={customerId} onValueChange={setCustomerId}>
            <SelectTrigger className="h-9 w-[220px] text-sm bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i clienti</SelectItem>
              {customers.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.code} — {c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2">
            <Label htmlFor="anon" className="text-sm text-slate-700 cursor-pointer">Anonimizza nomi</Label>
            <Switch id="anon" checked={anonymized} onCheckedChange={setAnonymized} />
          </div>
        </div>
      </div>

      {data.length === 0 ? (
        <div className="text-center py-14 bg-white rounded-xl border border-dashed border-slate-300">
          <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <div className="text-sm font-medium text-slate-700">Nessun dato di confronto disponibile</div>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <ResponsiveContainer width="100%" height={Math.max(220, data.length * 40)}>
            <BarChart data={data} layout="vertical" margin={RANKING_CHART_MARGIN}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis type="number" tick={AXIS_TICK_11} stroke="#94a3b8" />
              <YAxis type="category" dataKey={anonymized ? 'operator_alias' : 'operator_name'} tick={AXIS_TICK_12} stroke="#475569" width={80} />
              <Tooltip
                contentStyle={TOOLTIP_STYLE}
                formatter={(v) => [fmtEur(v), '€/ora']}
              />
              <Bar dataKey="avg_eur_per_hour" fill="#3b82f6" name="€/ora" radius={BAR_RADIUS_RIGHT} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default ProductionDashboard;
